import type {IStoreShort} from "@/api/aj-trends.model.ts";
import type {IProductPriceHistogram} from "@/api/histogram.model.ts";
import {type SharedCacheService, useSharedCache} from "@/service/sharedCache.service.ts";
import {withPageToken} from "@/service/pageToken.service.ts";

export interface ApiService {
  fetchProductAvailableAtStores(productHash: string): Promise<ReadonlyArray<IStoreShort>>

  fetchProductPriceHistogram(productHash: string, boundsFilter: string): Promise<IProductPriceHistogram>

  fetchStoreData(id: number): Promise<void>;
}

export class RateLimitExceeded extends Error {
  constructor() {
    super("rate-limit-exceeded");
  }
}

export function useApiService(): ApiService {
  const sharedCache = useSharedCache()
  return new FetchApiService(sharedCache)
}

class FetchApiService implements ApiService {
  constructor(private readonly sharedCache: SharedCacheService) {
  }

  async fetchProductAvailableAtStores(productHash: string): Promise<ReadonlyArray<IStoreShort>> {
    const cacheKey = `productAvailableAtStores[${productHash}]`
    return this.sharedCache.getOrCompute(cacheKey, async () => {
      const params = new URLSearchParams()
      params.set('p', productHash)

      const endpoint = `/api/productAvailableAtStores.json?${params}`
      const response = await this.fetchAndValidate(endpoint, {method: 'GET'})
      return await response.json() as Array<IStoreShort>
    })
  }

  async fetchProductPriceHistogram(productHash: string, boundsFilter: string): Promise<IProductPriceHistogram> {
    const params = new URLSearchParams()
    params.set('p', productHash)
    params.set('g', boundsFilter)

    const endpoint = `/api/productPriceHistogram.json?${params}`
    const response = await this.fetchAndValidate(endpoint, {method: 'GET'})
    return await response.json() as IProductPriceHistogram
  }

  async fetchStoreData(id: number): Promise<void> {
    const params = new URLSearchParams()
    params.set('s', String(id))

    const endpoint = `/api/storeData.json?${params}`
    await this.fetchAndValidate(endpoint, {method: 'GET'})
  }

  private async fetchAndValidate(input: RequestInfo | URL, init: RequestInit) {
    const response = await fetch(input, withPageToken(init))
    checkRateLimitExceeded(response)
    return response
  }
}

function checkRateLimitExceeded(response: Response) {
  // Special status 420 is returned by server when rate-limit
  // for client IP address has been exceeded
  if (response.status == 420) {
    throw new RateLimitExceeded()
  }
}
